import React, { useState } from "react";
import { User, Building, FileText, Image as ImageIcon } from "lucide-react";

interface ImagePlaceholderProps {
  src?: string;
  alt: string;
  type?: "avatar" | "building" | "document" | "general";
  className?: string;
  fallbackClassName?: string;
}

const ImagePlaceholder: React.FC<ImagePlaceholderProps> = ({
  src,
  alt,
  type = "general",
  className = "",
  fallbackClassName = "",
}) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleImageError = () => {
    setImageError(true);
    setIsLoading(false);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const getPlaceholderIcon = () => {
    switch (type) {
      case "avatar":
        return User;
      case "building":
        return Building;
      case "document":
        return FileText;
      default:
        return ImageIcon;
    }
  };

  const PlaceholderIcon = getPlaceholderIcon();

  const getPlaceholderColors = () => {
    switch (type) {
      case "avatar":
        return "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400";
      case "building":
        return "bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400";
      case "document":
        return "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400";
      default:
        return "bg-slate-100 text-slate-600 dark:bg-slate-900/30 dark:text-slate-400";
    }
  };

  // If no src provided or image failed to load, show placeholder
  if (!src || imageError) {
    return (
      <div
        className={`flex items-center justify-center ${getPlaceholderColors()} ${className} ${fallbackClassName}`}
        data-oid="c-73u2e"
      >
        <PlaceholderIcon
          className="w-1/2 h-1/2 max-w-8 max-h-8"
          data-oid="m4_o6e2"
        />
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} data-oid="zpez0c0">
      {isLoading && (
        <div
          className={`absolute inset-0 flex items-center justify-center ${getPlaceholderColors()} animate-pulse`}
          data-oid="ea3l7_1"
        >
          <PlaceholderIcon
            className="w-1/2 h-1/2 max-w-8 max-h-8"
            data-oid="2b9-j8y"
          />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`${isLoading ? "opacity-0" : "opacity-100"} transition-opacity duration-300 w-full h-full object-cover`}
        onError={handleImageError}
        onLoad={handleImageLoad}
        data-oid="c07q_gb"
      />
    </div>
  );
};

export default ImagePlaceholder;
